import React, { useEffect, useRef } from 'react';

import SelectField from '~/components/inputs/select-field';
import Button from '~/components/button';
import TextareaField from '~/components/inputs/textarea-field';
import Collapsible from '~/components/collapsible';
import { Field, Form } from '~/components/form';
import { valid, required } from '~/utils/validation';
import { api } from '~/api';
import { useFlashMessage } from '~/features/flash-messages/flash-message-actions';
import { shuffle } from '~/utils/functions';
import { captureException } from '~/utils/exception-tracking';
import {
  analyticsBeacon,
  DeprecatedEventType,
  EventType,
} from '~/utils/analytics';

import styles from '~/components/modals/modal.module.scss';

/**
 * Choices for "how did you hear about us?"
 * Key is the value stored in the DB, value is the display text shown to the user
 */
const LEAD_SOURCES = {
  tv_streaming_commercial: 'TV / Streaming Commercial',
  podcast: 'Podcast',
  radio: 'Radio',
  in_the_mail: 'In the Mail',
  instagram_or_facebook_ad: 'Instagram or Facebook ad',
  someone_i_follow_on_instagram: 'Someone I follow on Instagram',
  someone_i_follow_on_tiktok: 'Someone I follow on TikTok',
  someone_i_follow_on_youtube: 'Someone I follow on YouTube',
  family_or_friend: 'Family or friend',
  google_search: 'Google search',
  youtube_ad: 'YouTube Ad',
  tiktok_ad: 'TikTok Ad',
  online_article_or_blog: 'Online Article or blog',
  nextdoor: 'Nextdoor',
  retail_store: 'Retail Store (Walmart, Lowes, etc.)',
  other: 'Other',
};

/**
 * Returns a random-order array of LEAD_SOURCES keys
 * with "other" always at the end
 */
function getRandomizedLeadSources() {
  const keysWithoutOther = Object.keys(LEAD_SOURCES).filter(
    (k) => k !== 'other'
  );

  const randomKeys = shuffle(keysWithoutOther);
  randomKeys.push('other');

  return randomKeys;
}

const CheckoutSurveyForm = ({ isOpen, onClose, cart, transition }) => {
  const { showFlashMessage } = useFlashMessage();

  // Use a ref so we don't re-shuffle
  const leadSources = useRef(getRandomizedLeadSources()).current;

  useEffect(() => {
    if (isOpen) {
      analyticsBeacon.emit(DeprecatedEventType.CUSTOM, {
        category: 'Post Purchase Survey',
        action: 'Show Modal',
      });
      analyticsBeacon.emit(EventType.SURVEY_STARTED, {
        surveyName: 'Post Purchase Survey',
      });
    }
  }, [isOpen]);

  const handleSubmit = async (formValues) => {
    try {
      await api.purchaseOrders.postPurchaseSurvey({
        purchaseOrderUuid: cart?.uuid,
        leadSource: formValues.leadSource,
        otherText:
          formValues.leadSource === 'other' ? formValues.otherText : null,
      });

      analyticsBeacon.emit(DeprecatedEventType.CUSTOM, {
        category: 'Post Purchase Survey',
        action: 'Submit',
        label: formValues.leadSource,
      });
      analyticsBeacon.emit(EventType.SURVEY_COMPLETED, {
        surveyName: 'Post Purchase Survey',
        leadSource: formValues.leadSource,
        otherText:
          formValues.leadSource === 'other' ? formValues.otherText : null,
      });
    } catch (err) {
      // Don't tell users when this fails, just let them continue on with their lives
      captureException(new Error('Post purchase survey failed to submit'), {
        extras: {
          error: JSON.stringify(err),
        },
      });
    }

    showFlashMessage('Thanks for your response!', 'success');
    transition();
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>
        Thanks for <br />
        signing up!
      </h2>

      <p className={styles.text}>
        You just took the first step toward a healthier, happy lawn &mdash; and
        a healthier, happier you. Like your lawn we’re still growing, and you
        can help us improve by answering one quick question:
      </p>

      <Form onSubmit={handleSubmit} validate={validate}>
        {({ formValues, isSubmitting }) => (
          <>
            <p className={styles.postPurchaseQuestionHeading}>
              How did you hear about Sunday?
            </p>

            <Field
              aria-label="how did you hear about Sunday?"
              name="leadSource"
              component={SelectField}
              placeholder="Please select one"
            >
              {leadSources.map((leadSource) => (
                <option key={leadSource} value={leadSource}>
                  {LEAD_SOURCES[leadSource]}
                </option>
              ))}
            </Field>

            <Collapsible isOpen={formValues.leadSource === 'other'}>
              <div style={{ padding: '1px' }}>
                <Field
                  name="otherText"
                  component={TextareaField}
                  placeholder="Tell us more about how you found Sunday"
                  rows="3"
                />
              </div>
            </Collapsible>

            <div className={styles.buttons}>
              <Button type="submit" fullWidth isLoading={isSubmitting}>
                Submit
              </Button>
            </div>
          </>
        )}
      </Form>
    </div>
  );
};

function validate(formValues) {
  return {
    leadSource: valid(required())(formValues.leadSource),
  };
}

export default CheckoutSurveyForm;
